import React from 'react';
import { Link } from 'react-router-dom';
import { AppLayout } from '../components/layout/AppLayout';
import { FlamingoLogo, FloatingFlamingo } from '../components/common/FlamingoBrand';
import { PFIFullLogo, PFITextLogo, BrandGradient } from '../components/common/PFILogo';

export default function HomePage() {
  return (
    <AppLayout>
      <div className="relative min-h-screen">
        {/* Bold Hero Section with Massive Logo */}
        <div className="relative bg-gradient-to-br from-pink-50 via-white to-orange-50 rounded-3xl shadow-2xl p-16 mb-12 overflow-hidden">
          {/* Enhanced decorative background elements */}
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-pink-200/30 to-transparent rounded-full -translate-y-64 translate-x-64"></div>
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-tr from-orange-200/30 to-transparent rounded-full translate-y-48 -translate-x-48"></div>
          <div className="absolute top-1/2 left-1/2 w-[800px] h-[800px] bg-gradient-to-r from-pink-100/20 to-orange-100/20 rounded-full transform -translate-x-1/2 -translate-y-1/2"></div>
          
          <div className="relative z-10 text-center py-16">
            {/* Massive Logo Display */}
            <div className="mb-12">
              <PFIFullLogo size="2xl" className="mx-auto drop-shadow-2xl transform hover:scale-105 transition-transform duration-500" />
            </div>
            
            {/* Bold Welcome Message */}
            <div className="max-w-4xl mx-auto">
              <h1 className="text-7xl font-bold bg-gradient-to-r from-pink-600 via-pink-500 to-orange-500 bg-clip-text text-transparent mb-6 leading-tight">
                PFI Connect
              </h1>
              <h2 className="text-3xl font-semibold text-gray-700 mb-6">
                Project Management Excellence
              </h2>
              <p className="text-xl text-gray-600 font-medium mb-4 leading-relaxed">
                Your comprehensive platform for managing Pink Flamingo Interiors projects with style and efficiency
              </p>
              <p className="text-2xl text-pink-600 font-medium italic">
                &ldquo;Fun and Fabulous without the Fuss&rdquo; ✨
              </p>
            </div>
            
            {/* Quick Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mt-12">
              <Link
                to="/projects"
                className="group relative inline-flex items-center px-10 py-4 bg-gradient-to-r from-pink-500 to-orange-400 text-white font-bold text-xl rounded-2xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
              >
                <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                View Projects
              </Link>
              
              <Link
                to="/ai-dashboard"
                className="group relative inline-flex items-center px-10 py-4 bg-white text-pink-600 font-bold text-xl rounded-2xl border-2 border-pink-300 shadow-xl hover:shadow-2xl hover:bg-pink-50 transition-all duration-300"
              >
                <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                AI Intelligence
              </Link>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
          <div className="bg-white rounded-2xl shadow-xl border border-pink-100 p-8 pfi-card relative overflow-hidden group">
            {/* Card background gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-pink-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            
            <div className="relative z-10">
              <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-orange-400 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                <svg className="w-8 h-8 text-white drop-shadow-sm" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Project Management 🎨</h3>
              <p className="text-gray-600">
                Track and manage all your interior design projects from conception to completion with style.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl border border-blue-100 p-8 pfi-card relative overflow-hidden group">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            
            <div className="relative z-10">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                <svg className="w-8 h-8 text-white drop-shadow-sm" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Client Communication 💬</h3>
              <p className="text-gray-600">
                Keep customers informed with automated notifications and real-time project updates.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl border border-green-100 p-8 pfi-card relative overflow-hidden group">
            <div className="absolute inset-0 bg-gradient-to-br from-green-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            
            <div className="relative z-10">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-teal-500 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                <svg className="w-8 h-8 text-white drop-shadow-sm" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Supplier Coordination 🏗️</h3>
              <p className="text-gray-600">
                Seamlessly coordinate with suppliers and track delivery schedules with ease.
              </p>
            </div>
          </div>
        </div>

        {/* Success Message for Existing Users */}
        <div className="relative bg-gradient-to-r from-green-500 via-teal-500 to-blue-500 rounded-3xl shadow-2xl p-8 overflow-hidden">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="relative z-10 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">You're All Set Up! 🎉</h2>
            <p className="text-white/90 text-lg">
              Your PFI Connect platform is ready to manage all your interior design projects with Pink Flamingo style.
            </p>
          </div>
        </div>
        
        <FloatingFlamingo />
        
        {/* Access Links - Small discrete links */}
        <div className="text-center mt-16 pt-8 border-t border-gray-200">
          <div className="flex items-center justify-center space-x-8">
            <Link
              to="/supplier-login"
              className="inline-flex items-center text-sm text-gray-500 hover:text-blue-600 transition-colors duration-200"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              Supplier Portal
            </Link>
            <Link
              to="/auth/signin"
              className="inline-flex items-center text-sm text-gray-500 hover:text-pink-600 transition-colors duration-200"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-3.586l6.879-6.88a6 6 0 018.242.001M9 10h6m-3-3v6" />
              </svg>
              Admin Access
            </Link>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}